import { create } from 'zustand';

/**
 * Extract first frame from video and create thumbnail (square) and banner (full-width)
 */
const extractVideoFrames = async (videoFile) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    video.onloadedmetadata = async () => {
      // Seek to first frame
      video.currentTime = 0;
    };

    video.onseeked = async () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Get original video dimensions
        const videoWidth = video.videoWidth;
        const videoHeight = video.videoHeight;

        // Create square thumbnail (cropped from center)
        const thumbSize = Math.min(videoWidth, videoHeight);
        canvas.width = thumbSize;
        canvas.height = thumbSize;

        const sx = (videoWidth - thumbSize) / 2;
        const sy = (videoHeight - thumbSize) / 2;

        ctx.drawImage(video, sx, sy, thumbSize, thumbSize, 0, 0, thumbSize, thumbSize);

        const thumbnailBlob = await new Promise((res) => {
          canvas.toBlob(res, 'image/jpeg', 0.9);
        });

        // Create full-width banner (uncropped)
        canvas.width = videoWidth;
        canvas.height = videoHeight;
        ctx.drawImage(video, 0, 0, videoWidth, videoHeight);

        const bannerBlob = await new Promise((res) => {
          canvas.toBlob(res, 'image/jpeg', 0.9);
        });

        // Convert blobs to files
        const thumbnailFile = new File([thumbnailBlob], 'thumbnail.jpg', { type: 'image/jpeg' });
        const bannerFile = new File([bannerBlob], 'banner.jpg', { type: 'image/jpeg' });

        // Cleanup
        URL.revokeObjectURL(video.src);

        resolve({ thumbnailFile, bannerFile });
      } catch (error) {
        reject(error);
      }
    };

    video.onerror = () => {
      reject(new Error('Failed to load video'));
    };

    video.src = URL.createObjectURL(videoFile);
  });
};

const useGalleriesStore = create((set, get) => ({
  // State
  galleries: [],
  currentGallery: null,
  isLoading: false,
  error: null,
  uploadProgress: {},

  // Actions
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  clearError: () => set({ error: null }),

  // Fetch user's galleries
  fetchGalleries: async (accessToken, profileId = null) => {
    set({ isLoading: true, error: null });

    const url = profileId ? `/api/galleries?profileId=${profileId}` : '/api/galleries';

    try {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch galleries');
      }

      set({
        galleries: data.galleries || [],
        isLoading: false,
      });

      return { success: true, galleries: data.galleries };
    } catch (error) {
      set({
        isLoading: false,
        error: error.message,
      });
      return { success: false, error: error.message };
    }
  },

  // Fetch single gallery with media
  fetchGallery: async (galleryId, accessToken) => {
    set({ isLoading: true, error: null });

    try {
      const response = await fetch(`/api/galleries/${galleryId}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch gallery');
      }

      set({
        currentGallery: data.gallery,
        isLoading: false,
      });

      return { success: true, gallery: data.gallery };
    } catch (error) {
      set({
        isLoading: false,
        error: error.message,
      });
      return { success: false, error: error.message };
    }
  },

  // Create new gallery
  createGallery: async (galleryData, accessToken) => {
    set({ isLoading: true, error: null });

    try {
      const response = await fetch('/api/galleries', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(galleryData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create gallery');
      }

      // Add new gallery to list
      set((state) => ({
        galleries: [data.gallery, ...state.galleries],
        isLoading: false,
      }));

      return { success: true, gallery: data.gallery };
    } catch (error) {
      set({
        isLoading: false,
        error: error.message,
      });
      return { success: false, error: error.message };
    }
  },

  // Update gallery
  updateGallery: async (galleryId, galleryData, accessToken) => {
    set({ isLoading: true, error: null });

    try {
      const response = await fetch(`/api/galleries/${galleryId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(galleryData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update gallery');
      }

      // Update gallery in list
      set((state) => ({
        galleries: state.galleries.map((g) =>
          g.id === galleryId ? data.gallery : g
        ),
        currentGallery: state.currentGallery?.id === galleryId ? data.gallery : state.currentGallery,
        isLoading: false,
      }));

      return { success: true, gallery: data.gallery };
    } catch (error) {
      set({
        isLoading: false,
        error: error.message,
      });
      return { success: false, error: error.message };
    }
  },

  // Delete gallery
  deleteGallery: async (galleryId, accessToken) => {
    set({ isLoading: true, error: null });

    try {
      const response = await fetch(`/api/galleries/${galleryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete gallery');
      }

      // Remove gallery from list
      set((state) => ({
        galleries: state.galleries.filter((g) => g.id !== galleryId),
        currentGallery: state.currentGallery?.id === galleryId ? null : state.currentGallery,
        isLoading: false,
      }));

      return { success: true };
    } catch (error) {
      set({
        isLoading: false,
        error: error.message,
      });
      return { success: false, error: error.message };
    }
  },

  // Add media to gallery with multiple file upload support
  addMedia: async (galleryId, files, accessToken, onProgress) => {
    set({ error: null });

    try {
      // Validate files
      if (!files || !Array.isArray(files) || files.length === 0) {
        return { success: false, error: 'No files provided' };
      }

      const formData = new FormData();
      const videoFiles = [];

      // Separate videos from other files
      for (let i = 0; i < files.length; i++) {
        if (files[i].type.startsWith('video/')) {
          videoFiles.push(files[i]);
        } else {
          formData.append('files[]', files[i]);
        }
      }

      let uploadedMedia = [];

      // Upload images and non-video files first
      if (formData.has('files[]')) {
        const response = await fetch(`/api/galleries/${galleryId}/media`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
          body: formData,
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to upload media');
        }

        uploadedMedia = data.uploaded || [];
      }

      // Process videos: upload video, extract frames, upload thumbnail and banner, link them
      for (const videoFile of videoFiles) {
        try {
          // Extract thumbnail and banner from video
          const { thumbnailFile, bannerFile } = await extractVideoFrames(videoFile);

          // Upload video
          const videoFormData = new FormData();
          videoFormData.append('files[]', videoFile);

          const videoResponse = await fetch(`/api/galleries/${galleryId}/media`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
            body: videoFormData,
          });

          const videoData = await videoResponse.json();

          if (!videoResponse.ok) {
            throw new Error(videoData.message || 'Failed to upload video');
          }

          const uploadedVideo = videoData.uploaded?.[0];

          if (uploadedVideo) {
            // Upload thumbnail
            const thumbFormData = new FormData();
            thumbFormData.append('files[]', thumbnailFile);

            const thumbResponse = await fetch(`/api/galleries/${galleryId}/media`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${accessToken}`,
              },
              body: thumbFormData,
            });

            const thumbData = await thumbResponse.json();
            const uploadedThumb = thumbData.uploaded?.[0];

            // Upload banner
            const bannerFormData = new FormData();
            bannerFormData.append('files[]', bannerFile);

            const bannerResponse = await fetch(`/api/galleries/${galleryId}/media`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${accessToken}`,
              },
              body: bannerFormData,
            });

            const bannerData = await bannerResponse.json();
            const uploadedBanner = bannerData.uploaded?.[0];

            // Link thumbnail to video
            if (uploadedThumb) {
              await fetch(`/api/media/${uploadedVideo.uuid}/thumbnail/${uploadedThumb.uuid}`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${accessToken}`,
                },
              });
            }

            // Link banner to video
            if (uploadedBanner) {
              await fetch(`/api/media/${uploadedVideo.uuid}/banner/${uploadedBanner.uuid}`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${accessToken}`,
                },
              });
            }

            uploadedMedia.push(uploadedVideo);
          }
        } catch (videoError) {
          console.error('Failed to process video:', videoError);
          // Continue with other videos
        }
      }

      // Update current gallery with new media
      if (get().currentGallery?.id === galleryId) {
        set((state) => ({
          currentGallery: {
            ...state.currentGallery,
            media: [...(state.currentGallery.media || []), ...uploadedMedia],
          },
        }));
      }

      return { success: true, media: uploadedMedia };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  // Reorder media in gallery
  reorderMedia: async (galleryId, mediaOrder, accessToken) => {
    try {
      const response = await fetch(`/api/galleries/${galleryId}/media/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ mediaOrder }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to reorder media');
      }

      return { success: true };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  // Delete media from gallery
  deleteMedia: async (galleryId, assetId, accessToken) => {
    set({ error: null });

    try {
      const response = await fetch(`/api/galleries/${galleryId}/media/${assetId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete media');
      }

      // Remove media from current gallery
      if (get().currentGallery?.id === galleryId) {
        set((state) => ({
          currentGallery: {
            ...state.currentGallery,
            media: (state.currentGallery.media || []).filter((m) => m.id !== assetId),
          },
        }));
      }

      return { success: true };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  // Clear current gallery
  clearCurrentGallery: () => set({ currentGallery: null }),
}));

export { useGalleriesStore };
